import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Download, FileText, Package, Filter } from 'lucide-react';
import { deliveriesApi } from '@/services/deliveriesApi';
import { useToast } from '@/hooks/use-toast';
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious } from '@/components/ui/pagination';

interface DeliveryListItem {
  id: number;
  reference: string;
  scheduled_date?: string;
  delivery_address: string;
  state: string;
  status_label: string;
  status_color: 'blue' | 'yellow' | 'green' | 'red';
}

export const DeliveriesList = () => {
  const { token } = useAuth();
  const [deliveries, setDeliveries] = useState<DeliveryListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalDeliveries, setTotalDeliveries] = useState(0);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const { toast } = useToast();

  const pageSize = 20;

  const fetchDeliveries = async () => {
    if (!token) {
      console.log('No token available for loading deliveries');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await deliveriesApi.getDeliveries(token, currentPage, pageSize);
      
      let filteredDeliveries = response.items;
      if (statusFilter !== 'all') {
        filteredDeliveries = response.items.filter(delivery => delivery.state === statusFilter);
      }
      
      setDeliveries(filteredDeliveries);
      setTotalDeliveries(response.total);
      setTotalPages(Math.ceil(response.total / pageSize));
    } catch (error) {
      console.error('Error fetching deliveries:', error);
      toast({
        title: "Erreur",
        description: "Impossible de charger les livraisons.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchDeliveries();
    }
  }, [currentPage, statusFilter, token]);

  const handleDownloadSlip = async (deliveryId: number, reference: string) => {
    try {
      const blob = await deliveriesApi.downloadDeliverySlip(token!, deliveryId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${reference}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast({
        title: "Téléchargement réussi",
        description: `Le bon de livraison ${reference} a été téléchargé.`,
      });
    } catch (error) {
      console.error('Error downloading delivery slip:', error);
      toast({
        title: "Erreur",
        description: "Impossible de télécharger le bon de livraison.",
        variant: "destructive",
      });
    }
  };

  const exportToCSV = () => {
    const headers = ['Référence', 'Date programmée', 'Adresse de livraison', 'Statut'];
    const csvContent = [
      headers.join(','),
      ...deliveries.map(delivery => [
        delivery.reference,
        delivery.scheduled_date ? new Date(delivery.scheduled_date).toLocaleDateString('fr-FR') : '',
        `"${delivery.delivery_address}"`,
        delivery.status_label
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'mes-livraisons.csv';
    link.click();
    
    toast({
      title: "Export réussi",
      description: "La liste des livraisons a été exportée en CSV.",
    });
  };

  const getStatusBadgeVariant = (color: string) => {
    switch (color) {
      case 'green': return 'default';
      case 'blue': return 'secondary';
      case 'yellow': return 'outline';
      case 'red': return 'destructive';
      default: return 'secondary';
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            Mes Livraisons
          </CardTitle>
          <div className="flex gap-2">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-48">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Filtrer par statut" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les statuts</SelectItem>
                <SelectItem value="draft">Brouillon</SelectItem>
                <SelectItem value="waiting">En attente</SelectItem>
                <SelectItem value="confirmed">Confirmé</SelectItem>
                <SelectItem value="assigned">Assigné</SelectItem>
                <SelectItem value="done">Livré</SelectItem>
                <SelectItem value="cancel">Annulé</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={exportToCSV} variant="outline" size="sm">
              <FileText className="h-4 w-4 mr-2" />
              Exporter CSV
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {deliveries.length === 0 ? (
          <div className="text-center py-8">
            <Package className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">Aucune livraison trouvée.</p>
          </div>
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Référence</TableHead>
                  <TableHead>Date programmée</TableHead>
                  <TableHead>Adresse de livraison</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {deliveries.map((delivery) => (
                  <TableRow key={delivery.id}>
                    <TableCell className="font-medium">{delivery.reference}</TableCell>
                    <TableCell>{formatDate(delivery.scheduled_date)}</TableCell>
                    <TableCell className="max-w-xs truncate" title={delivery.delivery_address}>
                      {delivery.delivery_address}
                    </TableCell>
                    <TableCell>
                      <Badge variant={getStatusBadgeVariant(delivery.status_color)}>
                        {delivery.status_label}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDownloadSlip(delivery.id, delivery.reference)}
                        disabled={delivery.state !== 'done'}
                        title={delivery.state !== 'done' ? 'Bon de livraison disponible uniquement pour les livraisons terminées' : 'Télécharger le bon de livraison'}
                      >
                        <Download className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-6">
                <p className="text-sm text-muted-foreground">
                  Affichage de {(currentPage - 1) * pageSize + 1} à {Math.min(currentPage * pageSize, totalDeliveries)} sur {totalDeliveries} livraisons
                </p>
                <Pagination>
                  <PaginationContent>
                    <PaginationItem>
                      <PaginationPrevious 
                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                        className={currentPage === 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                      />
                    </PaginationItem>
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      const page = i + 1;
                      return (
                        <PaginationItem key={page}>
                          <PaginationLink
                            onClick={() => setCurrentPage(page)}
                            isActive={currentPage === page}
                            className="cursor-pointer"
                          >
                            {page}
                          </PaginationLink>
                        </PaginationItem>
                      );
                    })}
                    <PaginationItem>
                      <PaginationNext 
                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                        className={currentPage === totalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                      />
                    </PaginationItem>
                  </PaginationContent>
                </Pagination>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};